import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { Appearance } from "react-native";
import { StatusBar } from "expo-status-bar";
import {
  DarkTheme,
  ThemeProvider as NavigationThemeProvider,
} from "@react-navigation/native";
import { useAtom } from "jotai/react";
import { colorScheme, useColorScheme } from "nativewind";

import { VnBox } from "@/components/VnBox";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { themeAtom } from "@/stores/atoms";
import { palette } from "@/theme/palette";
import { THEME_VARS } from "@/theme/theme";
import { setAndroidNavigationBar } from "@/utils/android";

import { ThemeContext } from "./ThemeContext";

export type ColorSchemeType = "light" | "dark" | "system";

type ThemeProviderType = {
  children: ReactNode;
};

type ThemeType = Exclude<ColorSchemeType, "system">;

export const ThemeProvider = ({ children }: ThemeProviderType) => {
  const { colorScheme: systemColorScheme } = useColorScheme(); // from device
  const [storedTheme, setStoredTheme] = useAtom(themeAtom);
  const { isNative, isWeb } = usePlatformOS();

  const theme = (storedTheme ?? "system") as ColorSchemeType;

  const resolvedTheme = useMemo(() => {
    return theme === "system" ? (systemColorScheme ?? "light") : theme;
  }, [systemColorScheme, theme]);

  const resolvedColors = useMemo(() => palette[resolvedTheme], [resolvedTheme]);

  useEffect(() => {
    const initTheme = storedTheme ?? "light";

    if (!storedTheme) {
      setStoredTheme(initTheme);
    }

    colorScheme.set(initTheme as ThemeType);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = useCallback(
    (t: ThemeType) => {
      if (isNative) {
        Appearance.setColorScheme(t);
        void setAndroidNavigationBar(t);
      }

      setStoredTheme(t);
    },
    [setStoredTheme, isNative],
  );

  const isDarkMode = resolvedTheme === "dark";

  useEffect(() => {
    // Update web body classes when dark mode changes
    if (isWeb && typeof document !== "undefined") {
      if (isDarkMode) {
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
      }
    }
  }, [isDarkMode, isWeb]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        resolvedColors,
        isDarkMode,
      }}>
      <NavigationThemeProvider
        value={{
          ...DarkTheme,
          dark: isDarkMode,
          colors: {
            primary: resolvedColors.primary,
            background: resolvedColors.background,
            card: resolvedColors.card,
            text: resolvedColors.foreground,
            border: resolvedColors.border,
            notification: resolvedColors.destructive,
          },
        }}>
        <StatusBar style={isDarkMode ? "light" : "dark"} />
        <VnBox style={THEME_VARS[resolvedTheme]} className="flex-1">
          {children}
        </VnBox>
      </NavigationThemeProvider>
    </ThemeContext.Provider>
  );
};
